import { useState, useCallback } from 'react';
import { PlayerProfile, Outcome } from '../types';
import { EXP_PER_LEVEL } from '../constants';
import { supabase } from '../utils/supabase';

export const useProfile = () => {
  const [profile, setProfile] = useState<PlayerProfile>({
    name: '',
    level: 1,
    exp: 0,
  });

  /** Fetch the latest profile from DB using the stored player ID. */
  const refreshProfile = useCallback(async () => {
    const playerId = localStorage.getItem('dicepit_playerId');
    if (!playerId) return;

    const { data, error } = await supabase
      .from('players')
      .select('name, level, exp')
      .eq('id', playerId)
      .single();

    if (error) {
      console.warn('[Profile] Failed to refresh from DB:', error.message);
      return;
    }

    if (data) {
      setProfile(prev => ({
        ...prev,
        name: data.name || prev.name,
        level: data.level ?? prev.level,
        exp: data.exp ?? prev.exp,
      }));
    }
  }, []);

  /**
   * Record a practice game result (single player only).
   * For multiplayer, the server calls record_game_result — use refreshProfile() instead.
   */
  const addExp = useCallback((outcome: Outcome, playerScore: number, opponentScore: number): number => {
    // Optimistic EXP estimate for immediate UI feedback
    let gainedExp = 0;
    if (outcome === 'VICTORY') {
      const diffBonus = profile.level <= 5 ? 50 : profile.level <= 10 ? 100 : 200;
      const scoreBonus = Math.max(0, playerScore - opponentScore) * 2;
      gainedExp = diffBonus + scoreBonus;
    } else if (outcome === 'DEFEAT') {
      gainedExp = -40;
    } else {
      gainedExp = 10;
    }

    // Call RPC and then refresh profile from DB
    const playerId = localStorage.getItem('dicepit_playerId');
    if (playerId) {
      supabase
        .rpc('record_game_result', {
          p_player_id: playerId,
          p_outcome: outcome,
          p_my_score: playerScore,
          p_their_score: opponentScore,
        })
        .then(({ data, error }) => {
          if (error) {
            console.warn('[Profile] RPC record_game_result failed:', error.message);
            return;
          }
          if (data) {
            const result = typeof data === 'string' ? JSON.parse(data) : data;
            setProfile(prev => ({
              ...prev,
              level: result.new_level ?? prev.level,
              exp: result.new_exp ?? prev.exp,
            }));
          }
        });
    }

    return gainedExp;
  }, [profile.level]);

  const expThreshold = profile.level * EXP_PER_LEVEL;
  const expProgress = (profile.exp / expThreshold) * 100;

  return { profile, setProfile, addExp, refreshProfile, expThreshold, expProgress };
};
